/**
 * Clickable AI source chips. File citations open in StylishViewFileDialog.
 */
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';

import { useFetchCitationFileUrl } from '../hooks/useFetchCitationFileUrl';
import type { FieldDraftCitation } from './types';

type FileModalState = {
  show: boolean;
  fileUrl: string;
  fileName: string;
  mimetype: string;
  page?: number;
};

const EMPTY_MODAL: FileModalState = {
  show: false,
  fileUrl: '',
  fileName: '',
  mimetype: '',
};

export const ClickableFieldCitations: React.FC<{
  citations?: FieldDraftCitation[];
}> = ({ citations }) => {
  const fetchFileUrl = useFetchCitationFileUrl();
  const [modal, setModal] = useState<FileModalState>(EMPTY_MODAL);
  const [openingId, setOpeningId] = useState<string | null>(null);

  if (!citations?.length) {
    return (
      <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-muted-foreground">
        No sources
      </p>
    );
  }

  const openCitation = async (citation: FieldDraftCitation, index: number) => {
    if (!citation.fileId) return;
    const key = citation.fileId;
    setOpeningId(key);
    try {
      const file = await fetchFileUrl(citation.fileId);
      setModal({
        show: true,
        fileUrl: file.url,
        fileName: file.fileName,
        mimetype: file.mimetype,
        page: citation.page,
      });
    } catch {
      toast.error(`Could not open source ${index + 1}.`);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <>
      <ul
        className="tw:m-0 tw:mt-1 tw:flex tw:list-none tw:flex-wrap tw:gap-1 tw:p-0"
        aria-label="AI sources"
      >
        {citations.map((citation, index) => {
          const label = citation.sourceName ?? citation.sourceType ?? 'Source';
          const page = citation.page != null ? ` p.${citation.page}` : '';
          const clickable = !!citation.fileId;
          const opening = openingId === citation.fileId;
          return (
            <li key={`${citation.fileId ?? label}-${index}`}>
              {clickable ? (
                <button
                  type="button"
                  className="tw:rounded tw:bg-muted tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:text-muted-foreground tw:underline-offset-2 tw:hover:underline tw:disabled:no-underline"
                  title={citation.quote}
                  disabled={opening}
                  onClick={() => void openCitation(citation, index)}
                >
                  [{index + 1}] {label}
                  {page}
                </button>
              ) : (
                <span
                  className="tw:rounded tw:bg-muted tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:text-muted-foreground"
                  title={citation.quote}
                >
                  [{index + 1}] {label}
                  {page}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {modal.show ? (
        <StylishViewFileDialog
          show={modal.show}
          onClose={() => setModal(EMPTY_MODAL)}
          fileUrl={modal.fileUrl}
          fileName={modal.fileName}
          page={modal.page}
          mimetype={modal.mimetype}
        />
      ) : null}
    </>
  );
};

export default ClickableFieldCitations;
